import type { TemplateDefinition, TemplateFieldDescriptor, TemplateStep } from '../../common/overview/models/templateDefinition.types';

/**
 * The RFQ Template kind.
 *
 * Eleven steps: five describing the BID form an Initiator fills in (BID Details
 * carries Budget Reference directly above Description; the declaration is the
 * platform's, drawn on the New BID form, not configured here), six -- under
 * the "Supplier Bid Form" heading -- describing the response a supplier answers
 * with. Every visible word says BID; the kind, feature and section keys stay
 * `RFQ` / `rfq-…`, as the platform stores them. Every label, type, note and mandatory default is read off
 * `TemplatesLogic` in `NiraxoBid_Template.html`.
 *
 * The only kind that collects a Template Category, and the only one that lists
 * it as a column.
 */

/** Step 3's title, "Purchase Request" spelled out (2026-10-21) -- once "Add Product and PR" (read as this). */
export const PRODUCT_STEP_LABEL = 'Add Product and Purchase Request';

/* A header's key is its first wording's, so a retitle (With PR -> With Purchase Request, 2026-10-21) keeps it. */
const header = (label: string, keyLabel = label): TemplateFieldDescriptor => ({
  fieldKey: `header-${keyLabel.toLowerCase().replace(/[^a-z0-9]+/g, '-')}`,
  label,
  fieldType: 'Text',
  mandatory: false,
  isHeader: true,
});

/**
 * Step 3 is drawn as two parallel field sets, so its fields carry a group. Both read off the reference's
 * (2026-10-21): no Category -- the BID's own, on BID Details -- Quantity before UoM, "Product Description", and
 * Technical Specification reinstated as an optional Long text. Without PR is With PR less the PR picker.
 */
const productFields = (group: 'withPR' | 'withoutPR'): TemplateFieldDescriptor[] => {
  const fields: TemplateFieldDescriptor[] = [
    ...(group === 'withPR'
      ? [
        {
          fieldKey: 'purchase-request',
          label: 'Select Purchase Request',
          fieldType: 'Search and Select',
          helperText: 'Select existing Purchase Requests, then their Products',
          mandatory: false,
        } satisfies TemplateFieldDescriptor,
      ]
      : []),
    { fieldKey: 'product-name', label: 'Product Name', fieldType: 'Search and Select', helperText: 'Enter product name', mandatory: true },
    { fieldKey: 'product-code', label: 'Product Code', fieldType: 'Text', helperText: 'Optional', mandatory: false },
    { fieldKey: 'quantity', label: 'Quantity', fieldType: 'Number', helperText: 'Default: 1', mandatory: true },
    { fieldKey: 'uom', label: 'UoM', fieldType: 'Dropdown', helperText: 'Select unit of measurement', mandatory: true },
    { fieldKey: 'delivery-date', label: 'Delivery Date', fieldType: 'Date', helperText: 'DD/MM/YYYY', mandatory: true },
    /* The key stays `description` through the relabel, so stored overrides keep finding it. */
    { fieldKey: 'description', label: 'Product Description', fieldType: 'Long text', helperText: 'Optional', mandatory: false },
    { fieldKey: 'technical-specification', label: 'Technical Specification', fieldType: 'Long text', helperText: 'Optional', mandatory: false },
    { fieldKey: 'supporting-document', label: 'Supporting Document', fieldType: 'File upload', helperText: 'Optional', mandatory: false },
  ];
  /* Keys are unique within a section, so a group prefix keeps the two sets apart. */
  return fields.map((field) => ({ ...field, fieldKey: `${group}-${field.fieldKey}`, group }));
};

/* What the Location master fills in on Option A once a Delivery Location is chosen (2026-10-22). */
const AUTO_FILLED = 'Auto-filled from Delivery Location';

/**
 * Option A, a single location for the whole BID (2026-10-22): the location, a 10-digit Contact Number, the Delivery
 * Address the chosen location fills in, and optional instructions. No Delivery Date -- each Product carries its own.
 * No Country, State, City or PIN Code (removed 2026-10-28): the Delivery Address carries them.
 */
const singleLocationFields: TemplateFieldDescriptor[] = [
  { fieldKey: 'single-location', label: 'Delivery Location', fieldType: 'Dropdown', helperText: 'Select delivery location', mandatory: true },
  { fieldKey: 'single-contact', label: 'Contact Number', fieldType: 'Number', helperText: 'Enter 10-digit contact number', mandatory: true },
  { fieldKey: 'single-address', label: 'Delivery Address', fieldType: 'Long text', helperText: AUTO_FILLED, mandatory: true },
  { fieldKey: 'single-instructions', label: 'Delivery Instructions', fieldType: 'Long text', helperText: 'Optional', mandatory: false },
];

/**
 * Option B, a location per Product (2026-10-22): each labelled "(For Selected Product)" (2026-10-14) -- the location,
 * its 10-digit contact, the Products it receives, instructions and address. No Delivery Date.
 */
const SELECTED = ' (For Selected Product)';
const perProductFields: TemplateFieldDescriptor[] = [
  { fieldKey: 'per-product-location', label: `Delivery Location${SELECTED}`, fieldType: 'Dropdown', helperText: 'Select delivery location', mandatory: true },
  { fieldKey: 'per-product-contact', label: `Delivery Contact${SELECTED}`, fieldType: 'Number', helperText: 'Enter 10-digit contact number', mandatory: true },
  { fieldKey: 'per-product-products', label: `Products${SELECTED}`, fieldType: 'Multi-select Dropdown', helperText: 'Select Products', mandatory: true },
  { fieldKey: 'per-product-instructions', label: `Delivery Instructions${SELECTED}`, fieldType: 'Long text', helperText: 'Optional', mandatory: false },
  { fieldKey: 'per-product-address', label: `Delivery Address${SELECTED}`, fieldType: 'Long text', helperText: 'Enter delivery address', mandatory: true },
];

/*
  Every system field the kind declares Mandatory keeps its Mandatory switch on and disabled (product owner,
  2026-10-22): `mandatoryLocked`, so a save always sends it mandatory. It is NOT `locked` -- its edit pencil stays
  enabled, as every tile's is. Optional fields keep a working switch.
*/
const mandatorySwitchLocked = (steps: TemplateStep[]): TemplateStep[] =>
  steps.map((step) => ({
    ...step,
    fields: step.fields.map((field) =>
      field.mandatory && field.isHeader !== true ? { ...field, mandatoryLocked: true } : field,
    ),
  }));

export const rfqTemplateDefinition: TemplateDefinition = {
  kind: 'RFQ',
  feature: 'RFQ Template',
  path: 'rfq',
  icon: 'pi-file',
  title: 'BID Templates',
  /* The design's wording (2026-10-27). */
  subtitle: 'Configure and manage reusable BID and vendor bid form templates used across sourcing events.',
  /* The list's search, worded as on the PR and Supplier Onboarding templates (2026-10-27). */
  searchPlaceholder: 'Search templates by name',
  allStatusLabel: 'All Status',
  createSubtitle:
    'Configure a reusable BID template that Initiators will use when creating BIDs.',
  savedMessage: 'Initiators can now pick this template when creating a BID.',
  /* The ReviewAndSubmitPage_And_BIDOverview.html redesign (2026-10-21): design only. */
  bidLayout: true,
  firstSectionLabel: 'BID Details',
  pageSizeOptions: [5, 10, 25, 50],
  /* Excel and CSV only (2026-10-27): PDF and JSON are no longer offered. */
  exportFormats: ['xlsx', 'csv'],
  /*
    16-10-26.html's ImportPopup (2026-10-27): "1 Download template" naming the listing's columns -- the server's own
    import template for the kind -- then "2 Upload filled file".
  */
  importTemplateColumns: ['Template Name', 'Template ID', 'Category', 'Last Modified', 'Created By', 'Status', 'Description'],
  /* Save as Draft returns to the list with the top-right "Draft saved" toast, as the PR Template does (2026-10-27). */
  draftSavesInPlace: true,
  draftReturnsToList: true,
  exportSubtitle: 'Choose the rows and columns to include in the exported file.',
  deleteMessage: 'This template will be permanently removed. Records already created from it are unaffected.',
  savedSubtitle: 'Your BID template is ready to use.',
  updatedMessage:
    'Your changes are live. New BIDs created from this template will use the updated configuration; existing BIDs are not affected.',
  fieldRemovalConsequence: 'Initiators will no longer see it when creating a BID.',
  deactivateConsequence:
    'Initiators will no longer be able to select it for new BIDs. BIDs already created are unaffected.',
  activateConsequence: 'Initiators will be able to select it when creating a BID again.',
  blankNameFallback: 'BID template',
  collectsCategory: true,
  /* The entity's seeded default is neither edited nor deleted from the list; templates created or duplicated beside it are. */
  lockedTemplateName: 'Default BID Template',
  overviewSecondValue: 'category',
  namePlaceholder: 'e.g. Standard BID',
  descriptionPlaceholder: 'Describe when Initiators should use this template',
  informationHint: 'Name the template, describe its purpose and pick the category it applies to.',

  /* Template Code first, ahead of Template Name (BIDT-001, numbered per entity -- 2026-10-13). */
  leadingColumns: ['templateCode'],
  /* RFQ is the only kind with a Category column (TEMPLATE TYPE until 2026-10-21) and the only one without Number of Fields. */
  columns: [
    { key: 'templateCode', header: 'TEMPLATE CODE', label: 'Template code', minWidth: 160, renderer: 'text', sortable: true, filter: { comparison: 'Contains', kind: 'text' } },
    { key: 'templateCategory', header: 'CATEGORY', label: 'Category', minWidth: 150, renderer: 'categoryPill', sortable: true, filter: { comparison: 'Is', kind: 'choice' } },
    { key: 'modifiedDate', header: 'LAST MODIFIED', label: 'Last modified', minWidth: 200, renderer: 'text', sortable: true, filter: { comparison: 'Is after', kind: 'text' } },
    { key: 'createdBy', header: 'CREATED BY', label: 'Created by', minWidth: 180, renderer: 'avatar', sortable: true, filter: { comparison: 'Contains', kind: 'text' } },
    { key: 'statusName', header: 'STATUS', label: 'Status', minWidth: 130, renderer: 'statusPill', sortable: true, filter: { comparison: 'Is', kind: 'choice' } },
  ],

  groupHeadings: [{ beforeStepIndex: 5, label: 'Supplier Bid Form' }],

  /* The former separate declaration step: its added fields now live on BID Details (the statement is the platform's). */
  legacySections: { 'rfq-declaration': 'rfq-details' },
  /* Templates saved before the BID wording carry "RFQ" in their untouched defaults. */
  legacyWording: (text) =>
    text
      .replace(/\bRFQs\b/g, 'BIDs')
      .replace(/\bRFQ\b/g, 'BID')
      /* Step 3's title before "Purchase Request" was spelled out (2026-10-21); its key is unchanged. */
      .replace(/^Add Product and PR$/, PRODUCT_STEP_LABEL)
      /* The pricing footer's former wordings (2026-10-22): it reads "Final Total" alone. */
      .replace(/^(Final Total|Totals) Footer$/, 'Final Total')
      /* The intent's former Accept / Decline wording (2026-10-22). */
      .replace(/^Reason for Decline$/, 'Reason for Withdrawal')
      .replace(/^Accept \/ Decline · gates access to the remaining steps$/, 'Participate / Withdrawn'),

  /* What Template Information collects, listed under it in View -> Sections, so that section no longer reads "0 fields". */
  informationFields: [
    { label: 'Template Name', type: 'Text', required: true },
    { label: 'Template Category', type: 'Dropdown', required: true },
    { label: 'Business Unit', type: 'Dropdown', required: false },
    { label: 'Division', type: 'Dropdown', required: false },
    { label: 'Department', type: 'Dropdown', required: false },
    { label: 'Status', type: 'Toggle', required: false },
    { label: 'Description', type: 'Textarea', required: false },
  ],

  steps: mandatorySwitchLocked([
    {
      sectionKey: 'template-information',
      label: 'Template Information',
      icon: 'pi-info-circle',
      hint: 'Name the template, describe its purpose and pick the category it applies to.',
      fields: [],
    },
    {
      sectionKey: 'rfq-details',
      label: 'BID Details',
      icon: 'pi-file',
      hint: 'Set the core BID header fields Initiators complete when using this template.',
      panelHint:
        'These are the core identifiers Initiators fill in for every BID. System fields are always present — choose which are editable and add any BID-specific fields you need.',
      fields: [
        { fieldKey: 'rfq-title', label: 'BID Title', fieldType: 'Text', helperText: 'Enter BID title', mandatory: true },
        { fieldKey: 'start-date', label: 'BID Start Date', fieldType: 'Date', helperText: 'DD/MM/YYYY', mandatory: true },
        { fieldKey: 'bid-deadline', label: 'BID Deadline', fieldType: 'Date', helperText: 'DD/MM/YYYY', mandatory: true },
        { fieldKey: 'category', label: 'Category', fieldType: 'Dropdown', helperText: 'Select category', mandatory: true },
        { fieldKey: 'budget-reference', label: 'Budget Reference', fieldType: 'Text', helperText: 'Enter budget reference', mandatory: false },
        { fieldKey: 'description', label: 'BID Description', fieldType: 'Long text', helperText: 'Optional', mandatory: false },
        /* The BID's own documents (2026-10-22): the New BID form's drop zone, required when this is Mandatory. */
        { fieldKey: 'supporting-document', label: 'Supporting Document', fieldType: 'File upload', helperText: 'Optional · PDF, DOCX, XLSX, JPG, PNG up to 10 MB', mandatory: false },
      ],
    },
    {
      sectionKey: 'add-product-and-pr',
      label: PRODUCT_STEP_LABEL,
      icon: 'pi-box',
      hint: 'Let Initiators add Products with or without a Purchase Request, and configure the Product Detail fields.',
      panelHint:
        'Configure the Product Detail fields Initiators complete when adding Products, with or without a Purchase Request.',
      /* Product Sourcing -- With / Without Purchase Request / Mixed -- its first item (2026-10-21). */
      productSourcingChoice: true,
      /* "Purchase Request" spelled out (2026-10-21); the group keys, stored as each field's group, are unchanged. */
      groups: [
        {
          key: 'withPR',
          addLabel: 'Add Field to With Purchase Request',
          tag: 'With Purchase Request',
          subtitle: 'Fields shown when the Initiator adds products from an approved Purchase Request.',
        },
        {
          key: 'withoutPR',
          addLabel: 'Add Field to Without Purchase Request',
          tag: 'Without Purchase Request',
          subtitle: 'Fields shown when the Initiator adds products directly, without a Purchase Request.',
        },
      ],
      fields: [
        header('With Purchase Request', 'With PR'),
        ...productFields('withPR'),
        header('Without Purchase Request', 'Without PR'),
        ...productFields('withoutPR'),
      ],
    },
    {
      sectionKey: 'select-supplier',
      label: 'Select Supplier',
      icon: 'pi-users',
      hint: 'Configure how Initiators search, filter and select Suppliers from the Supplier Directory.',
      fields: [
        header('Supplier Directory Columns'),
        { fieldKey: 'supplier-code', label: 'Supplier Code', fieldType: 'Text', helperText: 'Directory column', mandatory: false },
        { fieldKey: 'supplier-location', label: 'Location', fieldType: 'Text', helperText: 'Directory column', mandatory: false },
        { fieldKey: 'supplier-email', label: 'Email', fieldType: 'Text', helperText: 'Directory column', mandatory: false },
        { fieldKey: 'supplier-risk-score', label: 'Risk Score', fieldType: 'Text', helperText: 'Directory column', mandatory: false },
        header('Selection Behaviour'),
        { fieldKey: 'supplier-search', label: 'Supplier Search', fieldType: 'Search and Select', helperText: 'Search by name or code', mandatory: false },
      ],
    },
    {
      sectionKey: 'delivery-instructions',
      label: 'Delivery Instructions',
      icon: 'pi-truck',
      hint: 'Define single-location or per-Product delivery configuration.',
      panelHint:
        'Configure a single delivery location for the whole BID, or per-Product delivery for multiple locations.',
      fields: [
        header('Option A — Single Location'),
        ...singleLocationFields,
        header('Option B — Multiple Locations'),
        ...perProductFields,
      ],
    },
    {
      sectionKey: 'rfq-overview-and-intent',
      label: 'BID Overview & Intent',
      icon: 'pi-file',
      hint: 'Read-only BID header shown to the Supplier, plus their Participate/Withdrawn intent to participate.',
      panelTitle: 'BID Overview & Intent',
      panelHint:
        'Read-only BID context plus the Participate/Withdraw intent that gates the rest of the form.',
      /* "Allow suppliers to withdraw" (2026-10-22), under the intent's answer cards. */
      withdrawChoice: true,
      fields: [
        header('1. BID Overview & Intent'),
        { fieldKey: 'rfq-number', label: 'BID Number', fieldType: 'Text', helperText: 'Read-only, system generated', mandatory: false },
        { fieldKey: 'overview-rfq-title', label: 'BID Title', fieldType: 'Text', helperText: 'From BID Details (Step 2), read-only', mandatory: false },
        { fieldKey: 'overview-rfq-type', label: 'BID Type', fieldType: 'Dropdown', helperText: 'Read-only · Procurement Category', mandatory: false },
        { fieldKey: 'overview-category', label: 'Category', fieldType: 'Dropdown', helperText: 'Read-only', mandatory: false },
        { fieldKey: 'overview-start-date', label: 'BID Start Date', fieldType: 'Date', helperText: 'Read-only', mandatory: false },
        { fieldKey: 'overview-bid-deadline', label: 'BID Deadline', fieldType: 'Date', helperText: 'Read-only · shows a live countdown', mandatory: false },
        { fieldKey: 'overview-description', label: 'BID Description', fieldType: 'Long text', helperText: 'Read-only', mandatory: false },
        { fieldKey: 'overview-attachments', label: 'BID Attachments', fieldType: 'File upload', helperText: 'Read-only · view & download buyer documents', mandatory: false },
        /* The answers' words (2026-10-22); the keys keep the decline wording the platform stores. */
        { fieldKey: 'intent-to-participate', label: 'Intent to Participate', fieldType: 'Radio', helperText: 'Participate / Withdrawn', mandatory: true },
        { fieldKey: 'reason-for-decline', label: 'Reason for Withdrawal', fieldType: 'Long text', helperText: 'Enter reason for withdrawal', mandatory: false },
      ],
    },
    {
      sectionKey: 'quotation-details',
      label: 'Quotation Details',
      icon: 'pi-dollar',
      hint: 'Set the quotation header and commercial terms the Supplier fills in once for the whole bid.',
      panelTitle: 'Quotation Details',
      panelHint: "The quotation header and the Supplier's commercial terms for this bid.",
      fields: [
        header('2. Quotation Details'),
        { fieldKey: 'quotation-reference', label: 'Supplier Quotation Reference No.', fieldType: 'Text', helperText: "Supplier's own document number", mandatory: false },
        { fieldKey: 'quotation-date', label: 'Quotation Date', fieldType: 'Date', helperText: "Auto-populated with today's date", mandatory: true },
        { fieldKey: 'quote-validity-date', label: 'Quote Validity Date', fieldType: 'Date', helperText: 'DD/MM/YYYY', mandatory: true },
        { fieldKey: 'currency', label: 'Currency', fieldType: 'Dropdown', helperText: 'From Currency master', mandatory: true },
        { fieldKey: 'payment-terms', label: 'Payment Terms', fieldType: 'Dropdown', helperText: 'From Payment Terms master', mandatory: false },
        { fieldKey: 'delivery-terms', label: 'Delivery Terms / Incoterms', fieldType: 'Dropdown', helperText: 'From Delivery Terms master', mandatory: false },
        { fieldKey: 'tax-registration', label: 'Tax Registration No. (GSTIN / VAT)', fieldType: 'Text', helperText: 'From the Supplier Onboarding record', mandatory: false },
      ],
    },
    {
      sectionKey: 'product-pricing',
      label: 'Product Pricing',
      icon: 'pi-money-bill',
      hint: 'Set the per-Product pricing fields Suppliers fill in for every requested Product.',
      panelTitle: 'Product Pricing',
      panelHint: 'The pricing the Supplier submits for each requested Product.',
      fields: [
        header('3. Product Pricing (per line)'),
        { fieldKey: 'pricing-product-name', label: 'Product Name', fieldType: 'Text', helperText: 'From the BID Product list (Step 3), read-only', mandatory: false },
        { fieldKey: 'required-quantity', label: 'Required Quantity', fieldType: 'Number', helperText: 'Read-only', mandatory: false },
        { fieldKey: 'quoted-quantity', label: 'Quoted Quantity', fieldType: 'Number', helperText: 'Defaults to the required quantity, flag when partial', mandatory: true },
        { fieldKey: 'rfq-uom', label: 'BID UoM', fieldType: 'Text', helperText: 'Read-only', mandatory: false },
        { fieldKey: 'required-delivery-date', label: 'Required Delivery Date', fieldType: 'Date', helperText: 'Read-only', mandatory: false },
        { fieldKey: 'unit-price', label: 'Unit Price', fieldType: 'Number', helperText: 'In the quote currency', mandatory: true },
        { fieldKey: 'discount', label: 'Discount (Percentage & Net Amount)', fieldType: 'Number', helperText: 'Optional', mandatory: false },
        { fieldKey: 'tax-gst', label: 'Tax / GST (Percentage)', fieldType: 'Dropdown', helperText: 'Tax amount auto-computed', mandatory: false },
        { fieldKey: 'freight-charges', label: 'Freight/Packing and Forwarding Charges', fieldType: 'Number', helperText: 'Configurable as line-level or header-level', mandatory: false },
        { fieldKey: 'other-charges', label: 'Other Charges', fieldType: 'Number', helperText: 'Optional', mandatory: false },
        { fieldKey: 'offered-delivery-date', label: 'Offered Delivery Date', fieldType: 'Date', helperText: 'Compared against the required delivery date', mandatory: true },
        { fieldKey: 'lead-time', label: 'Lead Time (days)', fieldType: 'Number', helperText: 'Optional', mandatory: false },
        { fieldKey: 'totals-footer', label: 'Final Total', fieldType: 'Calculated', helperText: 'Gross Value, Total Discount, Taxable Value, Total Tax, Freight & Other Charges, Grand Total', mandatory: false },
        { fieldKey: 'pricing-product-description', label: 'Product Description', fieldType: 'Long text', helperText: 'Read-only', mandatory: false },
        /* Remarks to Buyer (2026-10-30): moved here from Quotation Details; the supplier answers it once for the whole
           quotation, under the pricing table -- not per product. */
        { fieldKey: 'remarks-to-buyer', label: 'Remarks to Buyer', fieldType: 'Long text', helperText: 'Optional · answered once, under the pricing table', mandatory: false },
      ],
    },
    {
      sectionKey: 'delivery-commitment',
      label: 'Delivery Commitment',
      icon: 'pi-calendar',
      hint: 'Capture the delivery schedule Suppliers commit to in their response.',
      panelTitle: 'Delivery Commitment',
      panelHint: 'How much the Supplier will deliver to each location, and by when.',
      /* Per location and Product (2026-10-22): the BID's read-only values, then what the Supplier commits to. */
      fields: [
        header('4. Location Wise Delivery Commitment', '4. Delivery Commitment'),
        { fieldKey: 'commitment-location', label: 'Delivery Location', fieldType: 'Text', helperText: 'From the BID delivery instructions, read-only', mandatory: false },
        { fieldKey: 'commitment-product', label: 'Product', fieldType: 'Text', helperText: 'Read-only', mandatory: false },
        { fieldKey: 'commitment-required-quantity', label: 'Required Quantity', fieldType: 'Number', helperText: 'Read-only, per location', mandatory: false },
        { fieldKey: 'committed-quantity', label: 'Committed Quantity', fieldType: 'Number', helperText: 'Cannot exceed the quoted quantity', mandatory: true },
        { fieldKey: 'commitment-required-date', label: 'Required Delivery Date', fieldType: 'Date', helperText: 'Read-only', mandatory: false },
        { fieldKey: 'delivery-lead-time', label: 'Lead Time (days)', fieldType: 'Number', helperText: 'Days needed to deliver', mandatory: false },
        { fieldKey: 'delivery-remarks', label: 'Delivery Remarks', fieldType: 'Long text', helperText: 'Optional', mandatory: false },
      ],
    },
    {
      sectionKey: 'compliance-and-documents',
      label: 'Compliance & Documents',
      icon: 'pi-shield',
      hint: 'List the certificates and documents Suppliers must attach.',
      panelTitle: 'Compliance & Documents',
      panelHint: 'Supporting proof and certifications the Supplier attaches to the bid.',
      fields: [
        header('5. Compliance & Documents'),
        { fieldKey: 'certifications', label: 'Certifications', fieldType: 'File upload', helperText: 'Multiple files allowed', mandatory: false },
        { fieldKey: 'supporting-attachment', label: 'Supporting Attachment', fieldType: 'File upload', helperText: 'Optional', mandatory: false },
        { fieldKey: 'clarification-query', label: 'Clarification / Query to Buyer', fieldType: 'Long text', helperText: 'Optional', mandatory: false },
      ],
    },
    {
      sectionKey: 'supplier-declaration',
      label: 'Supplier Declaration',
      icon: 'pi-pencil',
      hint: 'Add the declaration Suppliers accept when submitting their bid.',
      panelTitle: 'Supplier Declaration',
      panelHint: 'Final acknowledgement before the bid is submitted.',
      /*
        The reference's separate declaration panel -- a statement textarea, two
        toggles and an Initiator preview -- is NOT built: its render flag is
        hardcoded false and it never appears. The declaration reaches the UI only
        as this one system field's helper text.
      */
      fields: [
        header('6. Supplier Declaration'),
        {
          fieldKey: 'acknowledgement-checkbox',
          label: 'Acknowledgement Checkbox',
          fieldType: 'Checkbox',
          helperText:
            'I am authorized to submit this quotation. All information and prices are accurate, independently prepared without collusion, valid until the stated date, and comply with the BID terms and all applicable laws. I understand false information may void this quotation and any resulting Purchase Order',
          mandatory: true,
        },
      ],
    },
  ]),
};
